import { DeliveryStatusResponse } from 'api/definitions/delivery-tracking';
import { FailedOrderPaymentResponse } from 'api/definitions/failed-order-payments';
import orders, { Order } from 'api/definitions/orders';
import { handleTimeoutError } from 'redux/modules/order/actions/errors';
import { isEligibleForDeliveryTracking } from 'redux/modules/order/selectors/is-eligible-for-delivery-tracking';
import { isOrderFailedPayment } from 'redux/modules/order/selectors/is-order-failed-payment';
import {
  GET_PENDING_ORDERS,
  GET_PENDING_ORDERS_FAILED,
  GET_PENDING_ORDERS_LOADED,
  GET_PENDING_ORDERS_LOADING,
} from 'redux/modules/orders/actions/types';
import { getFailedOrderPayment } from 'redux/modules/payment/actions/failed-order-payment/get-failed-order-payment';
import { isUserLoggedIn } from 'redux/modules/sessions/selectors';
import { CUSTOMER_ORDERS_SCHEMA } from 'redux/schemas';
import { getFeatureFlags } from 'utils/feature-flags';
import { log } from 'utils/error-logging';
import { getDeliveryStatus } from './get-delivery-status';

export interface GetPendingOrdersArgs {
  start?: number;
  size?: number;
  iHandleStatusCodes?: number[];
}

const isEligibleForFailedPayment = (order: Order): boolean =>
  isOrderFailedPayment(order.status, order.slots?.[0]?.startDateTime);

export default ({
    start = 0,
    size = 15,
    iHandleStatusCodes,
  }: GetPendingOrdersArgs = {}): WtrThunkAction<Promise<unknown>> =>
  (dispatch, getState) => {
    if (!isUserLoggedIn(getState())) return Promise.resolve();
    const { checkout_wismo: checkoutWismoEnabled } = getFeatureFlags();

    dispatch({ type: GET_PENDING_ORDERS_LOADING });
    return dispatch({
      types: GET_PENDING_ORDERS.triplet,
      apiCall: orders.get({
        iHandleStatusCodes,
        size,
        sortBy: '+',
        start,
        statuses: 'AMENDING+FULFIL+PAID+PAYMENT_FAILED+PICKED+PLACED',
      }),
      schema: CUSTOMER_ORDERS_SCHEMA,
    })
      .then(data => {
        const allPromises: Promise<DeliveryStatusResponse | FailedOrderPaymentResponse | null>[] =
          [];

        data.content
          .filter(isEligibleForFailedPayment)
          .map(order => allPromises.push(dispatch(getFailedOrderPayment(order.customerOrderId))));

        if (checkoutWismoEnabled) {
          data.content
            .filter(order => isEligibleForDeliveryTracking(order.status, order.slots[0]))
            .map(order =>
              allPromises.push(
                dispatch(getDeliveryStatus(order.customerOrderId)).catch(err => {
                  log(err, { logger: 'orders/delivery-tracking-load', section: 'orders' });
                  return null;
                }),
              ),
            );
        }

        return Promise.all([allPromises]).finally(() =>
          dispatch({
            type: GET_PENDING_ORDERS_LOADED,
            result: data,
            payload: { origin: size === 1 ? 'GLP' : 'MY_ORDERS' },
          }),
        );
      })
      .catch(error => {
        dispatch({ type: GET_PENDING_ORDERS_FAILED });
        if (dispatch(handleTimeoutError(error))) return;

        throw error;
      });
  };
